import { Injectable, Injector, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';

import { selectUser } from '@core/store/auth/auth.selectors';
import { GALIA_ASSISTANT_WINDOW_URL } from '@core/tokens/galia-url.token';

import { BreadcrumbService } from '@shared/breadcrumb/breadcrumb.service';

import { AssistantWindowLink } from './assistant-window-link';
import { AssistantService, PanelSize } from './assistant.service';

/**
 * The Galia tab's side of a popped-out assistant: opens the chat window, hands the conversation
 * over to it and back, and keeps it told what the tab shows. The panel injects it in every window,
 * so the work only a tab should do waits for start(), which only the tab's layout calls.
 */
@Injectable({ providedIn: 'root' })
export class AssistantWindowHost {
  private readonly assistant = inject(AssistantService);
  private readonly link = inject(AssistantWindowLink);
  private readonly injector = inject(Injector);
  private readonly windowUrl = inject(GALIA_ASSISTANT_WINDOW_URL);

  readonly available = this.link.available;
  readonly mode = this.link.mode;
  readonly blocked = signal(false);
  private sizeBeforePopOut: PanelSize = 'thin';
  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;
    const crumbs = toSignal(this.injector.get(BreadcrumbService).getBreadcrumbs(), {
      initialValue: [],
      injector: this.injector
    });
    const user = this.injector.get(Store).selectSignal(selectUser);
    effect(
      () => {
        const page = {
          dataSpaceId: this.assistant.dataSpaceId(),
          pageContext: this.assistant.pageContext(),
          summary: this.assistant.contextSummary(),
          crumbs: crumbs().map((c) => ({ label: c.label, link: c.link }))
        };
        untracked(() => this.link.publish(page));
      },
      { injector: this.injector }
    );
    let previousUser: string | null | undefined;
    effect(
      () => {
        const name = user()?.userName ?? null;
        if (previousUser !== undefined && name !== previousUser)
          untracked(() => this.link.closeChild());
        previousUser = name;
      },
      { injector: this.injector }
    );
    effect(
      () => {
        if (!this.assistant.panelOpen()) untracked(() => this.blocked.set(false));
      },
      { injector: this.injector }
    );
    this.link.startTab({
      navigate: (url) => this.assistant.openInApp(url),
      dock: (activeId, draft) => {
        this.reopen();
        this.assistant.selectTab(activeId);
        this.assistant.draft.set(draft);
      },
      failed: () => this.reopen(),
      rejoined: () => this.assistant.panelOpen.set(false)
    });
  }

  popOut(): void {
    const dataSpaceId = this.assistant.dataSpaceId();
    if (dataSpaceId === null || this.assistant.anyStreaming()) return;
    this.assistant.saveNow();
    const result = this.link.popOut(
      (link) => `${this.windowUrl}/ds/${dataSpaceId}?link=${encodeURIComponent(link)}`
    );
    this.blocked.set(result === 'blocked');
    if (result === 'blocked') return;
    this.sizeBeforePopOut = this.assistant.panelSize();
    this.assistant.panelOpen.set(false);
  }

  focusChild(): void {
    this.link.focusChild();
  }

  private reopen(): void {
    this.assistant.panelSize.set(this.sizeBeforePopOut);
    this.assistant.panelOpen.set(true);
  }
}
